/**
 * useFocusTrap Hook - SPEC-019 Task 3.7
 *
 * Traps focus within a container element for modal accessibility.
 * - Tab cycles through focusable elements
 * - Shift+Tab cycles backwards
 * - Focus returns to trigger element on close
 *
 * @module features/ai-button/hooks/useFocusTrap
 * @see SPEC-019 Requirements 10.1, 10.2, 10.3, 10.5
 */

import { useEffect, useRef } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface UseFocusTrapOptions {
  /** Whether the focus trap is enabled */
  enabled: boolean;
  /** Ref to the container element */
  containerRef: React.RefObject<HTMLElement | null>;
  /** CSS selector for initial focus element */
  initialFocusSelector?: string;
  /** Ref to element that should receive focus on close */
  returnFocusRef?: React.RefObject<HTMLElement | null>;
}

// ============================================================================
// Constants
// ============================================================================

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Get all focusable elements within a container
 */
function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

/**
 * useFocusTrap Hook
 *
 * Traps keyboard focus within a container element.
 * Essential for modal accessibility (WCAG 2.1 AA).
 *
 * @param options - Hook options
 *
 * @example
 * ```tsx
 * const containerRef = useRef<HTMLDivElement>(null);
 * const triggerRef = useRef<HTMLButtonElement>(null);
 *
 * useFocusTrap({
 *   enabled: isOpen,
 *   containerRef,
 *   returnFocusRef: triggerRef,
 * });
 * ```
 */
export function useFocusTrap({
  enabled,
  containerRef,
  initialFocusSelector,
  returnFocusRef,
}: UseFocusTrapOptions): void {
  const previousActiveElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!enabled || !containerRef.current) return;

    const container = containerRef.current;

    // Store previous active element for focus restoration
    previousActiveElementRef.current = document.activeElement as HTMLElement;

    // Set initial focus
    const focusableElements = getFocusableElements(container);

    if (focusableElements.length > 0) {
      let initialFocusElement: HTMLElement | null = null;

      if (initialFocusSelector) {
        initialFocusElement = container.querySelector<HTMLElement>(initialFocusSelector);
      }

      if (!initialFocusElement && focusableElements[0]) {
        initialFocusElement = focusableElements[0];
      }

      initialFocusElement?.focus();
    }

    // Handle Tab key for focus trapping
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      const focusable = getFocusableElements(container);
      if (focusable.length === 0) return;

      const firstElement = focusable[0];
      const lastElement = focusable[focusable.length - 1];

      if (!firstElement || !lastElement) return;

      // Shift+Tab at first element -> go to last
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      // Tab at last element -> go to first
      if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
        return;
      }
    };

    container.addEventListener('keydown', handleKeyDown);

    // Cleanup: restore focus
    return () => {
      container.removeEventListener('keydown', handleKeyDown);

      // Restore focus to return element or previous element
      const returnElement = returnFocusRef?.current || previousActiveElementRef.current;
      returnElement?.focus();
    };
  }, [enabled, containerRef, initialFocusSelector, returnFocusRef]);
}
